/**
 * SnippetSelector - Bottom Sheet Component (Mobile-First)
 *
 * Displays available snippets for an agent in a mobile-friendly bottom sheet.
 * On desktop, adapts to a modal centered layout.
 */

import React, { useEffect, useState } from 'react'
import type { AgentSnippet } from '../../types/snippets'
import { snippetsApi } from '../../services/snippetsService'
import { IconButton } from '@/design-system/atoms/IconButton'
import { DynamicIcon, Icon } from '@/design-system/atoms/Icon'
import { Spinner } from '@/design-system/atoms/Spinner'
import { Alert } from '@/design-system/molecules/Alert'

interface SnippetSelectorProps {
  agentCode: string
  isOpen: boolean
  onClose: () => void
  onSnippetSelect: (snippet: AgentSnippet) => void
}

export const SnippetSelector: React.FC<SnippetSelectorProps> = ({
  agentCode,
  isOpen,
  onClose,
  onSnippetSelect,
}) => {
  const [snippets, setSnippets] = useState<AgentSnippet[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && snippets.length === 0) {
      loadSnippets()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, agentCode])

  const loadSnippets = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await snippetsApi.getSnippets(agentCode)
      setSnippets(data)
    } catch {
      setError('Falha ao carregar atalhos')
    } finally {
      setLoading(false)
    }
  }

  const categories = Array.from(new Set(snippets.map(s => s.category)))
  const filteredSnippets = selectedCategory
    ? snippets.filter(s => s.category === selectedCategory)
    : snippets

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[60] transition-opacity"
        onClick={onClose}
        data-testid="snippet-backdrop"
      />

      {/* Bottom Sheet */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-1/2 lg:-translate-x-1/2 lg:max-w-2xl lg:bottom-20 lg:rounded-2xl bg-white dark:bg-gray-800 shadow-xl z-[70] max-h-[80vh] overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Atalhos Rápidos
            </h3>
            <IconButton
              onClick={onClose}
              variant="ghost"
              size="sm"
              shape="rounded"
              aria-label="Fechar"
              data-testid="close-snippets"
            >
              <Icon name="x" size={20} />
            </IconButton>
          </div>

          {/* Category Pills */}
          {categories.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                  selectedCategory === null
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Todos
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                    selectedCategory === category
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-120px)] p-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" className="text-primary-600" />
            </div>
          )}

          {error && <Alert variant="error">{error}</Alert>}

          {!loading && !error && (
            <div className="space-y-2">
              {filteredSnippets.map(snippet => (
                <button
                  key={snippet.code}
                  onClick={() => {
                    onSnippetSelect(snippet)
                  }}
                  className="w-full text-left p-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all active:scale-[0.98]"
                  data-testid={`snippet-${snippet.code}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0">
                      <DynamicIcon
                        name={snippet.iconName}
                        fallbackValue={snippet.icon}
                        size={24}
                      />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {snippet.label}
                        </h4>
                        {snippet.category && (
                          <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full flex-shrink-0">
                            {snippet.category}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {snippet.description}
                      </p>
                    </div>
                    <Icon
                      name="chevron-right"
                      size={20}
                      className="text-gray-400 flex-shrink-0"
                    />
                  </div>
                </button>
              ))}
            </div>
          )}

          {!loading && !error && filteredSnippets.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Nenhum atalho disponível
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        @media (min-width: 1024px) {
          @keyframes slide-up {
            from {
              opacity: 0;
              transform: translate(-50%, 20px);
            }
            to {
              opacity: 1;
              transform: translate(-50%, 0);
            }
          }
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  )
}
